/**
 * @file 存放所有全域格式化相關的純 JavaScript 函式。
 *
 * =========================================================================
 * ==                           開發者注意事項 (Developer Notice)                           ==
 * =========================================================================
 *
 * 這個檔案中的所有函式，都會透過 /src/plugins/FormatterPlugin.js 自動註冊為
 * 全域的「Vue 實例方法」和「過濾器 (Filter)」。
 *
 * 當您在 Vue 元件中使用時：
 *
 * 1. 作為「實例方法」(在 <script> 中):
 * 名稱前需要加上「$」。
 * 範例： this.$transferDate('2025-01-01');
 *
 * 2. 作為「過濾器」(在 <template> 中):
 * 使用原始名稱。
 * 範例： <span>{{ someDate | transferDate }}</span>
 *
 * 因此，如果您在程式碼中看到 this.$someMethod 而想尋找原始定義，
 * 請記得在本檔案中搜尋不含「$」的原始函式名稱 (someMethod)。
 *
 */

/**
 * 根據性別代碼回傳對應的文字
 * @param {number|string} gender - 性別代碼 (1: 男性, 2: 女性)
 * @returns {string}
 */
export function getGenderText(genderKey) {
  const genderMap = { 
    1: "男性", 
    2: "女性" 
  };
  return genderMap[genderKey] || "";
}

/**
 * 取得格式化後的受益人關係文字。
 * @param {object} bnfData - 受益人資料，應包含 relation 及 relationOther 屬性。
 * @param {object} bnfEnum - 關係對應表 (例：{2: "配偶", 3: "子女", 4: "父母", 3: "兄弟姊妹", 6: "祖父母" 7: "其他"})
 * @returns {string} 格式化後的關係文字 (例：「父母」、「其他-叔叔」)。
 */
export function getBnfRelationText(bnfData, bnfEnum) {
  const relation = bnfData.relation;
  const relationOther = bnfData.relationOther;
  let relationText = bnfEnum[relation];

  // 當關係為「其他」時，附加自定義的文字
  if (relation === 7 && relationOther) {
      relationText += `-${relationOther}`;
  }
  return relationText;
}

/**
 * 將西元年或民國年日期字串，統一格式化為民國年 (YYY-MM-DD)
 * @param {string} date - 輸入的日期字串，支援 YYYY-MM-DD, YYY/MM/DD 等格式
 * @param {boolean} [keepTime=false] - 是否保留時間部分
 * @returns {string}
 */
export function transferDate(date, keepTime = false) {
  if (!date || typeof date !== 'string') {
    return "";
  }

  const generalDateRegex = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(.*)$/;
  const rocDateRegex = /^(\d{2,3})[-/](\d{1,2})[-/](\d{1,2})(.*)$/;

  let match = generalDateRegex.exec(date);
  if (match) {
    const year = parseInt(match[1], 10);
    if (year > 1911) {
      const rocYear = String(year - 1911).padStart(3, '0');
      const month = String(match[2]).padStart(2, '0');
      const day = String(match[3]).padStart(2, '0');
      const timePart = match[4] || "";
      let result = `${rocYear}-${month}-${day}`;
      if (keepTime) result += timePart;
      return result;
    }
  }

  match = rocDateRegex.exec(date);
  if (match) {
    const rocYear = String(match[1]).padStart(3, '0');
    const month = String(match[2]).padStart(2, '0');
    const day = String(match[3]).padStart(2, '0');
    const timePart = match[4] || "";
    let result = `${rocYear}-${month}-${day}`;
    if (keepTime) result += timePart;
    return result;
  }

  return date;
}

/**
 * 將民國年日期字串轉換為西元年 (YYYY-MM-DD)
 * @param {string} rocDate - 民國年日期字串，支援 YYY-MM-DD, YYY/MM/DD 等格式
 * @param {boolean} [keepTime=false] - 是否保留時間部分
 * @returns {string}
 */
export function toGeneralDate(date, keepTime = false) {
  if (!date || typeof date !== 'string') {
    return "";
  }
  const rocDateRegex = /^(\d{2,3})[-/](\d{1,2})[-/](\d{1,2})(.*)$/;
  const match = rocDateRegex.exec(date);
  if (match) {
    const generalYear = parseInt(match[1], 10) + 1911;
    const month = String(match[2]).padStart(2, '0');
    const day = String(match[3]).padStart(2, '0');
    const timePart = match[4] || "";
    let result = `${generalYear}-${month}-${day}`;
    if (keepTime) result += timePart;
    return result;
  }

  return date; // 格式不符則直接返回
}


/**
 * 移除金額中的逗號，用於傳值
 * @param {string|number} amount
 * @returns {string}
 */
export function removeComma(amount) {
  if (amount) {
    return amount.toString().replace(/,/g, "");
  }
  return "0";
}